import { useRef } from 'react';
import type { ReactNode } from 'react';

import {
  ContentChunkSetModule,
  StandardContentModule,
  TabsContentModule,
  SubModule,
} from '@silvertours/common-landingpages-view';

import { HasBadgeRules, Heading } from '../../element';
import { CollapsableContainer } from './CollapsableContainer';
import { CollapseButton } from './CollapseButton';
import { useCollapse } from './useCollapse';
import { SectionRows } from './SectionRows';
import { SectionTitle } from '../../element/SectionTitle';

export type CollapsableContentBlockRules = HasBadgeRules & {
  allowCollapse: boolean;
};

export type CollapsableContentBlockContent = Pick<
  StandardContentModule,
  'badge' | 'heading' | 'sections'
>;

type CollapsableContentBlockTranslations = {
  collapseButton: {
    showLessLabel: string;
    showMoreLabel: string;
  };
};

export type CollapsableContentBlockProps = {
  children: (
    section: SubModule<TabsContentModule | ContentChunkSetModule>,
  ) => ReactNode;
  content: CollapsableContentBlockContent;
  rules: CollapsableContentBlockRules;
  translations?: CollapsableContentBlockTranslations;
};

export const CollapsableContentBlock = ({
  children,
  content: { badge, heading, sections },
  rules: { allowCollapse },
  translations,
}: CollapsableContentBlockProps) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const { canBeCollapsed, isCollapsed, setIsCollapsed } = useCollapse(
    allowCollapse,
    ref,
  );

  const handleCollapseToggled = (isNowCollapsed: boolean) => {
    setIsCollapsed(isNowCollapsed);
  };

  const {
    collapseButton: { showLessLabel = '', showMoreLabel = '' },
  } = translations || { collapseButton: {} };

  return (
    <>
      <CollapsableContainer
        behavior={{ ref }}
        rules={{ canBeCollapsed, isCollapsed }}
      >
        <SectionTitle
          content={badge}
          rules={{
            sectionTitle: {
              style: 'default',
            },
            verticalSpacing: { isLargeAtMD: true },
          }}
        />
        <Heading content={heading} />
        <SectionRows sections={sections}>{children}</SectionRows>
      </CollapsableContainer>
      <CollapseButton
        behavior={{ ref, onCollapseToggled: handleCollapseToggled }}
        rules={{ canBeCollapsed, isCollapsed }}
        labels={{ showLess: showLessLabel, showMore: showMoreLabel }}
      />
    </>
  );
};
